import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../utils/useAuth';

export default function ManagerApprovals() {
  const { profile } = useAuth();
  const [users, setUsers] = useState([]);
  const [fields, setFields] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  
  // Team/School form
  const [teamName, setTeamName] = useState('');
  const [teamType, setTeamType] = useState('School');

  useEffect(() => {
    loadUsers();
    loadFields();
    loadTeams();
  }, [profile]);

  async function loadUsers() {
    if (!profile?.union) return;
    
    const snap = await getDocs(collection(db, 'users'));
    const allUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Filter users in this manager's union
    setUsers(allUsers.filter(u => u.union === profile.union));
  }

  async function loadFields() {
    const snap = await getDocs(collection(db, 'fields'));
    setFields(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function loadTeams() {
    const snap = await getDocs(collection(db, 'teams'));
    setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function approve(uid, role) {
    try {
      await updateDoc(doc(db, 'users', uid), {
        approved: true,
        role
      });

      alert('User approved');
      loadUsers();
    } catch (err) {
      console.error(err);
      alert('Approval failed');
    }
  }

  async function revoke(uid) {
    await updateDoc(doc(db, 'users', uid), { approved: false, role: 'pending' });
    alert('User revoked');
    loadUsers();
  }

  async function approveField(id) {
    try {
      await updateDoc(doc(db, 'fields', id), {
        approved: true,
        approvedBy: profile.fullName,
        rejectionReason: null,
      });
      alert('Field approved!');
      loadFields();
    } catch (err) {
      alert('Error approving field: ' + err.message);
    }
  }

  function openRejectModal(field) {
    setSelectedField(field);
    setRejectionNotes('');
    setShowRejectModal(true);
  }

  async function submitRejection() {
    if (!rejectionNotes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await updateDoc(doc(db, 'fields', selectedField.id), {
        approved: false,
        rejected: true,
        rejectionReason: rejectionNotes,
        rejectedBy: profile.fullName,
        rejectedAt: new Date(),
      });
      alert('Field rejected with notes sent to coach.');
      setShowRejectModal(false);
      setSelectedField(null);
      setRejectionNotes('');
      loadFields();
    } catch (err) {
      alert('Error rejecting field: ' + err.message);
    }
  }

  async function revokeFieldApproval(id) {
    if (!window.confirm('Revoke approval for this field?')) return;

    try {
      await updateDoc(doc(db, 'fields', id), {
        approved: false,
      });
      alert('Approval revoked.');
      loadFields();
    } catch (err) {
      alert('Error revoking approval: ' + err.message);
    }
  }

  async function addTeam(e) {
    e.preventDefault();
    
    if (!teamName.trim()) {
      alert('Please enter a team/school/club name');
      return;
    }

    try {
      await addDoc(collection(db, 'teams'), {
        name: teamName,
        type: teamType,
        union: profile.union,
        createdBy: profile.fullName,
        createdAt: serverTimestamp(),
      });
      alert(`${teamType} added successfully!`);
      setTeamName('');
      setTeamType('School');
      loadTeams();
    } catch (err) {
      alert('Error adding team: ' + err.message);
    }
  }

  async function deleteTeam(id) {
    if (!window.confirm('Delete this team/school/club?')) return;

    try {
      await deleteDoc(doc(db, 'teams', id));
      alert('Deleted successfully');
      loadTeams();
    } catch (err) {
      alert('Error deleting: ' + err.message);
    }
  }

  // Separate pending and approved users
  const pendingReferees = users.filter(u => u.requestedRole === 'referee' && !u.approved);
  const pendingCoaches = users.filter(u => u.requestedRole === 'coach' && !u.approved);
  const approvedReferees = users.filter(u => u.role === 'referee' && u.approved);
  const approvedCoaches = users.filter(u => u.role === 'coach' && u.approved);

  // Separate pending and approved fields
  const pendingFields = fields.filter(f => !f.approved && !f.rejected);
  const approvedFields = fields.filter(f => f.approved);

  // Group approved fields by venue
  const approvedByVenue = approvedFields.reduce((acc, field) => {
    if (!acc[field.venue]) {
      acc[field.venue] = [];
    }
    acc[field.venue].push(field);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Approvals Management</h2>
        <p className="text-gray-600 mt-1">Managing approvals for: <span className="font-semibold text-blue-700">{profile?.union}</span></p>
      </div>

      {/* REJECTION MODAL */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Reject Field Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              Rejecting: <span className="font-semibold">{selectedField?.venue} - {selectedField?.fieldName}</span>
            </p>
            
            <label className="block text-sm font-semibold mb-2">
              Reason for Rejection:
            </label>
            <textarea
              className="w-full p-3 border rounded mb-4 h-32 resize-none"
              placeholder="e.g., Fence too close to field, grandstand in the middle, venue not approved by union..."
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
            />

            <div className="flex gap-3">
              <button
                onClick={submitRejection}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedField(null);
                  setRejectionNotes('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== UNION USER APPROVALS ========== */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-6 border-b-2 border-blue-600 pb-2">Union User Approvals</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* PENDING REFEREES */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-yellow-700">Pending Referee Requests</h3>
          {pendingReferees.length === 0 ? (
            <p className="text-gray-500">No pending referee requests.</p>
          ) : (
            <div className="space-y-3">
              {pendingReferees.map(u => (
                <div key={u.id} className="bg-white p-4 rounded shadow border-l-4 border-yellow-500">
                  <div className="mb-3">
                    <div className="font-semibold text-lg">{u.fullName}</div>
                    <div className="text-sm text-gray-600 mt-1">Email: {u.email}</div>
                    <div className="text-sm text-gray-600">Phone: {u.phoneNumber || 'N/A'}</div>
                    <div className="text-sm text-gray-600">BokSmart: {u.boksmartNumber || 'N/A'}</div>
                    <div className="text-sm text-gray-600">League: {u.currentLeague || 'N/A'}</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(u.id, 'referee')}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      className="px-4 py-2 border border-gray-400 rounded hover:bg-gray-100"
                      onClick={() => revoke(u.id)}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PENDING COACHES */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-yellow-700">Pending Coach Requests</h3>
          {pendingCoaches.length === 0 ? (
            <p className="text-gray-500">No pending coach requests.</p>
          ) : (
            <div className="space-y-3">
              {pendingCoaches.map(u => (
                <div key={u.id} className="bg-white p-4 rounded shadow border-l-4 border-yellow-500">
                  <div className="mb-3">
                    <div className="font-semibold text-lg">{u.fullName}</div>
                    <div className="text-sm text-gray-600 mt-1">Email: {u.email}</div>
                    <div className="text-sm text-gray-600">Phone: {u.phoneNumber || 'N/A'}</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(u.id, 'coach')}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      className="px-4 py-2 border border-gray-400 rounded hover:bg-gray-100"
                      onClick={() => revoke(u.id)}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* APPROVED REFEREES */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-green-700">Approved Referees</h3>
          {approvedReferees.length === 0 ? (
            <p className="text-gray-500">No approved referees yet.</p>
          ) : (
            <div className="space-y-3">
              {approvedReferees.map(u => (
                <div key={u.id} className="bg-white p-3 rounded shadow border-l-4 border-green-600">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold">{u.fullName} <span className="text-sm text-gray-500">({u.email})</span></div>
                      <div className="text-sm text-gray-600">League: {u.currentLeague || 'N/A'}</div>
                    </div>
                    <button className="px-3 py-1 border rounded hover:bg-gray-100" onClick={() => revoke(u.id)}>Revoke</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* APPROVED COACHES */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-green-700">Approved Coaches</h3>
          {approvedCoaches.length === 0 ? (
            <p className="text-gray-500">No approved coaches yet.</p>
          ) : (
            <div className="space-y-3">
              {approvedCoaches.map(u => (
                <div key={u.id} className="bg-white p-3 rounded shadow border-l-4 border-green-600">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold">{u.fullName} <span className="text-sm text-gray-500">({u.email})</span></div>
                    </div>
                    <button className="px-3 py-1 border rounded hover:bg-gray-100" onClick={() => revoke(u.id)}>Revoke</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* ========== UNION FIELD APPROVALS ========== */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-6 border-b-2 border-blue-600 pb-2">Union Field Approvals</h2>

        {/* PENDING FIELDS */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-yellow-700">Pending Field Requests</h3>
          {pendingFields.length === 0 ? (
            <p className="text-gray-500">No pending field requests.</p>
          ) : (
            <div className="space-y-3">
              {pendingFields.map(f => (
                <div key={f.id} className="bg-white p-4 rounded shadow border-l-4 border-yellow-500">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-lg">{f.venue}</div>
                      <div className="text-gray-600">Field: {f.fieldName}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        Requested: {f.createdAt?.toDate().toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => approveField(f.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => openRejectModal(f)}
                        className="px-4 py-2 border border-gray-400 rounded hover:bg-gray-100"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* APPROVED FIELDS */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-green-700">Approved Fields</h3>
          {Object.keys(approvedByVenue).length === 0 ? (
            <p className="text-gray-500">No approved fields yet.</p>
          ) : (
            <div className="space-y-6">
              {Object.entries(approvedByVenue).map(([venueName, venueFields]) => (
                <div key={venueName} className="bg-white p-4 rounded shadow border-l-4 border-green-600">
                  <h4 className="font-bold text-lg mb-3">{venueName}</h4>
                  <div className="space-y-2">
                    {venueFields.map(f => (
                      <div key={f.id} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                        <div>
                          <span className="font-semibold">{f.fieldName}</span>
                          {f.approvedBy && (
                            <span className="text-xs text-gray-500 ml-2">
                              (Approved by: {f.approvedBy})
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => revokeFieldApproval(f.id)}
                          className="px-3 py-1 border border-gray-400 rounded hover:bg-gray-100 text-sm"
                        >
                          Revoke
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ========== SCHOOLS/CLUBS MANAGEMENT ========== */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-6 border-b-2 border-blue-600 pb-2">Schools/Clubs Management</h2>

        {/* ADD NEW TEAM/SCHOOL/CLUB */}
        <div className="bg-white p-6 rounded shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">Add New School/Club/Team</h3>
          <form onSubmit={addTeam} className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-2">Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="e.g., Paarl Boys High, Blue Bulls, etc."
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                required
              />
            </div>
            <div className="w-48">
              <label className="block text-sm font-semibold mb-2">Type</label>
              <select
                className="w-full p-2 border rounded"
                value={teamType}
                onChange={(e) => setTeamType(e.target.value)}
              >
                <option value="School">School</option>
                <option value="Club">Club</option>
                <option value="Team">Team</option>
              </select>
            </div>
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Add
            </button>
          </form>
        </div>

        {/* EXISTING TEAMS */}
        <div className="bg-white p-6 rounded shadow">
          <h3 className="text-lg font-semibold mb-4 text-blue-700">Existing Schools/Clubs/Teams</h3>
          {teams.length === 0 ? (
            <p className="text-gray-500">No teams/schools/clubs added yet.</p>
          ) : (
            <div className="space-y-2">
              {teams.map(team => (
                <div
                  key={team.id}
                  className="flex justify-between items-center bg-gray-50 p-3 rounded border-l-4 border-blue-500"
                >
                  <div>
                    <span className="font-semibold">{team.name}</span>
                    <span className="ml-3 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {team.type}
                    </span>
                    {team.union && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({team.union})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => deleteTeam(team.id)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
